import { Modal } from "antd";
import axios from "axios";
import React, { createContext, useContext, useMemo, useState } from "react";

import { useSession } from "@src/appV2/Auth/api/useSession";
import { logoutUserFromEverywhere } from "@src/appV2/Auth/api/logoutUserFromEverywhere";
import { useEffectivePermissions } from "@src/appV2/Auth/hooks/useEffectivePermissions";
import { environmentConfig } from "@src/appV2/environment";
import { useImpersonationSessions } from "@src/appV2/ImpersonationSessions/ImpersonationSessionsProvider";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { showErrorToast, showInfoToast, showSuccessToast } from "@src/appV2/lib/Notifications";

import { getErrorMessage } from "@src/appV2/api/errors";

import { useMintAdminMagicLink } from "./useMintAdminMagicLink";

const LOGOUT_FROM_ALL_DEVICES = {
  dialogTitle: "Log out from all devices?",
  dialogContent: (userName: string) =>
    `This will log ${userName} out of all devices immediately. Continue?`,
  okText: "Log Out From All Devices",
  userNameFallback: "this user",
  successToast: "User has been logged out of all devices.",
  permissionErrorToast: "You don't have permission to log this user out.",
  notFoundInfoToast: "This user has no Cognito account to log out of.",
  logErrorEvent: "Error in useWorkplaceUserAdminActions - logoutFromAllDevices",
} as const;

function showMintError(err: unknown): void {
  if (axios.isAxiosError(err)) {
    const status = err.response?.status;
    const responseData = err.response?.data as
      | { message?: string; retryAfterSeconds?: number }
      | undefined;
    const message = responseData?.message;

    if (status === 404) {
      showErrorToast(
        "No Cognito account for this email yet. Ask them to attempt login once — that'll provision the account, then this link will work."
      );
      return;
    }
    if (status === 422) {
      showErrorToast(message ?? "This user can't be issued a login link right now.");
      return;
    }
    if (status === 429) {
      const retryAfter = responseData?.retryAfterSeconds;
      showErrorToast(
        retryAfter !== undefined
          ? `Too many requests — try again in ${retryAfter} seconds.`
          : (message ?? "Too many requests. Try again shortly.")
      );
      return;
    }
    if (message) {
      showErrorToast(message);
      return;
    }
  }
  if (err instanceof Error) {
    showErrorToast(err.message);
    return;
  }
  showErrorToast("Failed to mint login link.");
}

export interface WorkplaceUserAdminActionsTarget {
  _id: string;
  name?: string;
  email: string;
}

export interface WorkplaceUserAdminActions {
  canImpersonate: boolean;
  canCopyLink: boolean;
  canLogoutFromEverywhere: boolean;
  impersonate: (user: WorkplaceUserAdminActionsTarget) => void;
  copyMagicLink: (email: string) => void;
  logoutFromEverywhere: (user: WorkplaceUserAdminActionsTarget) => void;
  pendingEmail: string | null;
}

export function useWorkplaceUserAdminActions(): WorkplaceUserAdminActions & {
  fallbackLink: string | null;
  dismissFallback: () => void;
} {
  const { openSession } = useImpersonationSessions();
  const { canImpersonateUsers, canGetWorkplaceUserMagicLink, canLogoutUserFromEverywhere } =
    useEffectivePermissions();
  const session = useSession();
  const mintMagicLink = useMintAdminMagicLink();
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [fallbackLink, setFallbackLink] = useState<string | null>(null);

  const impersonate = (user: WorkplaceUserAdminActionsTarget) => {
    if (!canImpersonateUsers) {
      return;
    }
    openSession(
      "workplace",
      environmentConfig.REACT_APP_WORKPLACE_APP_URL,
      user._id,
      user.name ?? user.email,
      user.email
    );
  };

  // Must run synchronously inside the click handler — awaiting the mint before
  // calling navigator.clipboard.write breaks the Safari user-gesture chain and
  // the write is silently rejected.
  const copyMagicLink = (email: string) => {
    setPendingEmail(email);

    const mintPromise = mintMagicLink.mutateAsync({
      email,
      requesterUrl: `${environmentConfig.REACT_APP_ADMIN_APP_CLIENT_URL}/v2/email-login-link`,
    });

    const supportsDeferredClipboardWrite =
      typeof ClipboardItem !== "undefined" && typeof navigator.clipboard?.write === "function";
    const writePromise = supportsDeferredClipboardWrite
      ? navigator.clipboard.write([
          new ClipboardItem({
            "text/plain": mintPromise.then(
              ({ loginLink }) => new Blob([loginLink], { type: "text/plain" })
            ),
          }),
        ])
      : undefined;
    writePromise?.catch(() => undefined);

    mintPromise
      .then(async ({ loginLink }) => {
        let copiedToClipboard = false;
        if (writePromise) {
          try {
            await writePromise;
            copiedToClipboard = true;
            showSuccessToast("Login link copied to clipboard");
          } catch {
            // fall through to the visible-link modal
          }
        }
        logEvent(APP_V2_APP_EVENTS.MINT_ADMIN_MAGIC_LINK_SUCCESS, {
          adminUserId: session?.adminProfile?.userId,
          adminEmail: session?.adminProfile?.email,
          targetEmail: email,
          copiedToClipboard,
        });
        if (!copiedToClipboard) {
          setFallbackLink(loginLink);
        }
      })
      .catch(showMintError)
      .finally(() => setPendingEmail(null));
  };

  const logoutFromEverywhere = (user: WorkplaceUserAdminActionsTarget) => {
    if (!canLogoutUserFromEverywhere) {
      return;
    }
    const userName = user.name ?? user.email ?? LOGOUT_FROM_ALL_DEVICES.userNameFallback;

    Modal.confirm({
      title: LOGOUT_FROM_ALL_DEVICES.dialogTitle,
      content: LOGOUT_FROM_ALL_DEVICES.dialogContent(userName),
      okText: LOGOUT_FROM_ALL_DEVICES.okText,
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await logoutUserFromEverywhere({ userId: user._id });
          showSuccessToast(LOGOUT_FROM_ALL_DEVICES.successToast);
        } catch (error: unknown) {
          if (axios.isAxiosError(error)) {
            const status = error.response?.status;
            if (status === 401 || status === 403) {
              showErrorToast(LOGOUT_FROM_ALL_DEVICES.permissionErrorToast);
              return;
            }
            if (status === 404) {
              showInfoToast(LOGOUT_FROM_ALL_DEVICES.notFoundInfoToast);
              return;
            }
          }
          logError(LOGOUT_FROM_ALL_DEVICES.logErrorEvent, { error });
          showErrorToast(getErrorMessage(error));
        }
      },
    });
  };

  return {
    canImpersonate: canImpersonateUsers,
    canCopyLink: canGetWorkplaceUserMagicLink,
    canLogoutFromEverywhere: canLogoutUserFromEverywhere,
    impersonate,
    copyMagicLink,
    logoutFromEverywhere,
    pendingEmail,
    fallbackLink,
    dismissFallback: () => setFallbackLink(null),
  };
}

const WorkplaceUserAdminActionsContext = createContext<WorkplaceUserAdminActions | null>(null);

export function useWorkplaceUserAdminActionsContext(): WorkplaceUserAdminActions | null {
  return useContext(WorkplaceUserAdminActionsContext);
}

interface WorkplaceUserAdminActionsProviderProps {
  children: React.ReactNode;
}

export function WorkplaceUserAdminActionsProvider(props: WorkplaceUserAdminActionsProviderProps) {
  const { children } = props;
  const {
    canImpersonate,
    canCopyLink,
    canLogoutFromEverywhere,
    impersonate,
    copyMagicLink,
    logoutFromEverywhere,
    pendingEmail,
    fallbackLink,
    dismissFallback,
  } = useWorkplaceUserAdminActions();

  const value = useMemo<WorkplaceUserAdminActions>(
    () => ({
      canImpersonate,
      canCopyLink,
      canLogoutFromEverywhere,
      impersonate,
      copyMagicLink,
      logoutFromEverywhere,
      pendingEmail,
    }),
    // impersonate/copyMagicLink/logoutFromEverywhere are stable enough — they
    // read fresh values from hooks via closure on each render — but listing
    // them avoids stale captures.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canImpersonate, canCopyLink, canLogoutFromEverywhere, pendingEmail]
  );

  return (
    <WorkplaceUserAdminActionsContext.Provider value={value}>
      {children}
      <Modal
        title="Oops, unable to copy link to clipboard"
        visible={fallbackLink !== null}
        onCancel={dismissFallback}
        footer={null}
      >
        <p style={{ marginBottom: "10px" }}>Please copy the following link:</p>
        <p>{fallbackLink}</p>
      </Modal>
    </WorkplaceUserAdminActionsContext.Provider>
  );
}
